// ABOUTME: Local NumericalInput (stepper) component matching the Fundamental Design System spec.
// ABOUTME: Uses Button (square/tertiary) + Icon for −/+ controls with a centred value and optional unit suffix.
import {Button, Icon} from '@hm/fundamental'
import {mdiMinus, mdiPlus} from '@mdi/js'
import React, {useCallback, useEffect, useState} from 'react'

/** Decimal places of a finite number, ignoring exponential notation. */
const decimalPlaces = (n: number): number => {
  if (!Number.isFinite(n)) return 0
  const s = String(n)
  if (s.includes('e') || s.includes('E')) return 0
  const dot = s.indexOf('.')
  return dot === -1 ? 0 : s.length - dot - 1
}

/**
 * Snap a computed result to the precision implied by the operands, removing IEEE-754
 * floating-point noise (e.g. 4.1 − 1 → 3.0999999999999996 becomes 3.1). The target precision is the
 * larger of the value's and step's decimal places, capped to keep typed values intact.
 */
export const snapToStepPrecision = (result: number, value: number, step: number): number => {
  if (!Number.isFinite(result)) return result
  const decimals = Math.min(Math.max(decimalPlaces(value), decimalPlaces(step)), 10)
  const factor = 10 ** decimals
  return Math.round(result * factor) / factor
}

export interface NumericalInputProps {
  /** Current numeric value */
  value: number
  /** Callback when value changes */
  onChange: (value: number) => void
  /** Minimum allowed value (inclusive) */
  min?: number
  /** Maximum allowed value (inclusive) */
  max?: number
  /** Step size for increment/decrement — defaults to 1 */
  step?: number
  /** Unit suffix displayed faded after the value — e.g. "h", "min" */
  suffix?: string
  /** Whether the control is disabled */
  disabled?: boolean
  /** Error message — shows red border + helper text */
  error?: string
  /** Forces the error outline without a message — e.g. when the error is shown in a tooltip */
  hasError?: boolean
  /** Test ID for Cypress */
  'data-cy'?: string
  /** Accessible name forwarded to the underlying input */
  'aria-label'?: string
  /** IDs of elements describing the input (e.g. error text) forwarded to the underlying input */
  'aria-describedby'?: string
  /** Formats the internal numeric value for display — e.g. minutes → "1:00" */
  formatValue?: (value: number) => string
  /** Parses a display string back to the internal numeric value — e.g. "1:00" → 60 */
  parseValue?: (display: string) => number | undefined
  /** Width preset — 'small' (120px, default) | 'medium' (160px) | 'large' (240px) */
  size?: 'small' | 'medium' | 'large'
  /** Called when the input loses focus */
  onBlur?: () => void
}

const SIZE_CLASSES: Record<string, string> = {
  small: 'w-[120px]',
  medium: 'w-[160px]',
  large: 'w-[240px]',
}

export const NumericalInput: React.FC<NumericalInputProps> = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
  disabled = false,
  error,
  hasError,
  'data-cy': dataCy,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  formatValue,
  parseValue,
  size = 'small',
  onBlur
}) => {
  const canDecrement = min === undefined || value - step >= min
  const canIncrement = max === undefined || value + step <= max

  const [inputValue, setInputValue] = useState(String(value))

  useEffect(() => {
    setInputValue(formatValue ? formatValue(value) : String(value))
  }, [value, formatValue])

  const handleDecrement = useCallback(() => {
    if (disabled || !canDecrement) return
    onChange(snapToStepPrecision(value - step, value, step))
  }, [disabled, canDecrement, value, step, onChange])

  const handleIncrement = useCallback(() => {
    if (disabled || !canIncrement) return
    onChange(snapToStepPrecision(value + step, value, step))
  }, [disabled, canIncrement, value, step, onChange])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      setInputValue(raw)
      if (parseValue) {
        const parsed = parseValue(raw)
        if (parsed !== undefined) onChange(parsed)
      } else {
        const parsed = Number(raw)
        if (raw !== '' && !Number.isNaN(parsed)) onChange(parsed)
      }
    },
    [onChange, parseValue]
  )

  const handleBlur = useCallback(() => {
    setInputValue(formatValue ? formatValue(value) : String(value))
  }, [value, formatValue])

  const outlineClass = (error || hasError)
    ? 'outline outline-1 outline-offset-[-1px] outline-negative'
    : 'outline outline-1 outline-offset-[-1px] outline-border_highlight_10 focus-within:outline-primary'

  return (
    <div className={`flex flex-col gap-1 shrink-0 ${SIZE_CLASSES[size]}`} data-cy={dataCy}>
      <div
        className={`flex items-center gap-1 h-10 rounded bg-white p-1 transition-colors ${outlineClass} ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
      >
        {/* Decrement */}
        <Button
          variant="tertiary"
          square
          size="small"
          type="button"
          disabled={disabled || !canDecrement}
          onClick={handleDecrement}
          aria-label="Decrease value"
          className="!p-2 !text-foreground"
        >
          <Icon path={mdiMinus} size="regular" />
        </Button>
        <div className="flex-1 flex items-center justify-center min-w-0">
          <div className="flex items-center gap-0.5 h-5">
            <input
              type={formatValue ? 'text' : 'number'}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={() => { handleBlur(); onBlur?.() }}
              onFocus={(e) => e.target.select()}
              disabled={disabled}
              className="min-w-[2ch] p-0 text-right font-medium text-base leading-6 text-foreground bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              style={{width: `${Math.max(2, inputValue.length + 0.5)}ch`}}
              aria-label={ariaLabel ?? 'Numeric value'}
              aria-describedby={ariaDescribedBy}
              aria-invalid={Boolean(error || hasError) || undefined}
            />
            {suffix && (
              <span className="shrink-0 text-base font-medium leading-6 text-foreground_faded">
                {suffix}
              </span>
            )}
          </div>
        </div>
        <Button
          variant="tertiary"
          square
          size="small"
          type="button"
          disabled={disabled || !canIncrement}
          onClick={handleIncrement}
          aria-label="Increase value"
          className="!p-2 !text-foreground"
        >
          <Icon path={mdiPlus} size="regular" />
        </Button>
      </div>

      {error && <p className="text-xs leading-4 text-negative px-3">{error}</p>}
    </div>
  )
}
